/**
 * TOKENIZE, the sponsor dashboard (UF-05, reframed for the economic-vs-legal issuance split).
 *
 * The sponsor is no longer just an applicant who submits a deed and waits. They originate the asset,
 * approve the trust-generated capital structure, hold an allocation, and decide when and how to bring
 * portions of it to market. So this is a portfolio, not a form: the assets they have with the trust,
 * where each has got to, what they have raised, and the tokens they hold as an investor.
 *
 * The rule the surface still makes visible: the sponsor never mints. A regulated trust values the asset,
 * forms the holding structure that takes legal title, issues the tokens, and allocates the initial
 * issuance back to the sponsor. This page shows that division of labour rather than hiding it.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  api,
  LipaApiError,
  type ApplicationSummary,
  type Holding,
  type SponsorAsset,
  type SponsorSummary,
  type TokenHolding,
} from '@/lib/api';
import { TierGate } from '@/components/gate';
import { PageHeader, SectionTitle, StatTile } from '@/components/dash';
import { StatusPill } from '@/components/status';
import { EmptyState, ErrorState, LoadingCard } from '@/components/states';
import { Reveal } from '@/components/motion';
import { useSession } from '@/lib/session-context';
import { formatUnits, STATUS_SUMMARY, statusTone } from '@/lib/tokenization';

const num = (n: number) => n.toLocaleString('en-KE');

const STAGE_LABEL: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  diligence: 'In diligence',
  valuation: 'In valuation',
  formation: 'Trust formation',
  allocation: 'Allocating',
  offering: 'Live',
};

export default function Tokenize() {
  return (
    <TierGate requires="T2" what="Tokenizing an asset">
      <Dashboard />
    </TierGate>
  );
}

function Dashboard() {
  const { me } = useSession();
  const [summary, setSummary] = useState<SponsorSummary | null>(null);
  const [assets, setAssets] = useState<SponsorAsset[]>([]);
  const [holdings, setHoldings] = useState<Holding[]>([]);

  // The LIFECYCLE. `applications` is the real record (migration 0055); `assets` above is the older
  // sponsor-issuance fixture surface, which has no backend and is left alone rather than half-migrated.
  const [applications, setApplications] = useState<ApplicationSummary[]>([]);
  const [tokens, setTokens] = useState<TokenHolding[]>([]);

  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!me) return;
    setError(null);
    try {
      const [s, a, h, apps, portfolio] = await Promise.all([
        api.sponsor.summary(),
        api.sponsor.assets(),
        api.holdings(me.participant_id),
        api.tokenization.applications(),
        api.tokenization.portfolio(),
      ]);
      setSummary(s);
      setAssets(a.assets);
      setHoldings(h);
      setApplications(apps);
      setTokens(portfolio);
    } catch (e) {
      setError(e instanceof LipaApiError ? e.detail.message : 'We could not load your assets.');
    } finally {
      setLoading(false);
    }
  }, [me]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <LoadingCard lines={4} />;
  if (error) return <ErrorState body={error} onRetry={() => void load()} />;

  const empty =
    assets.length === 0 &&
    holdings.length === 0 &&
    applications.length === 0 &&
    tokens.length === 0;

  // Applications waiting on the OWNER. Surfaced above everything else, because an offer nobody opens
  // is an asset that stops moving and an owner who never learns why.
  const needsYou = applications.filter(
    (a) => a.status === 'offer_pending_user_approval' || a.status === 'information_requested',
  );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Tokenize"
        subtitle="Assets you have brought to the trust, and the tokens you hold."
        actions={
          <Link
            to="/tokenize/new"
            className="rounded-field bg-primary px-3.5 py-2 text-sm font-medium text-white transition hover:bg-primary-light"
          >
            Submit an asset
          </Link>
        }
      />

      {/* How issuance works: the three actors, so the sponsor never wonders who mints. */}
      <section className="rounded-card border border-primary/20 bg-primary-tint p-4">
        <p className="text-sm leading-relaxed text-ink">
          You originate the asset. The <strong className="font-medium">holding entity</strong> takes
          legal title, a regulated <strong className="font-medium">trust</strong> values it and
          issues the tokens, and your initial allocation comes back to you. You then decide how much
          to bring to market.
        </p>
      </section>

      {/* SUMMARY. */}
      {summary ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile label="Assets" value={summary.assets} />
          <StatTile label="Trusts" value={summary.trusts} />
          <StatTile label="Capital raised" value={`KES ${summary.capitalRaised}`} accent />
          <StatTile label="Live offerings" value={summary.liveOfferings} />
        </div>
      ) : null}

      {/* WAITING ON YOU. First, and unmissable: an offer nobody opens is an asset that stops. */}
      {needsYou.length > 0 ? (
        <section className="space-y-2.5">
          {needsYou.map((a) => (
            <Reveal key={a.application_id}>
              <Link
                to={`/tokenize/applications/${a.application_id}`}
                className="block rounded-card border-2 border-primary bg-surface p-4 shadow-soft transition hover:bg-primary-tint"
              >
                <p className="text-micro font-medium uppercase tracking-wide text-brand">
                  Waiting for you
                </p>
                <p className="mt-1 truncate text-sm font-medium text-ink">{a.name}</p>
                <p className="mt-0.5 text-micro text-ink-muted">{STATUS_SUMMARY[a.status]}</p>
              </Link>
            </Reveal>
          ))}
        </section>
      ) : null}

      {/* LIFECYCLE LINKS. The sponsor is an active participant, not just an applicant. */}
      <nav className="grid grid-cols-4 gap-2.5">
        <Tile href="/tokenize/portfolio" label="Tokens" desc="What you hold" />
        <Tile href="/tokenize/offerings" label="Offerings" desc="Fundraising rounds" />
        <Tile href="/tokenize/distributions" label="Distributions" desc="Income paid out" />
        <Tile href="/credit" label="Financing" desc="Borrow against assets" />
      </nav>

      {/* THE APPLICATIONS. The real lifecycle record, with the stage tracker in miniature. */}
      {applications.length > 0 ? (
        <section>
          <SectionTitle>Applications</SectionTitle>
          <ul className="space-y-3">
            {applications.map((a) => {
              const done = a.stages.filter((s) => s.state === 'done').length;
              return (
                <li key={a.application_id}>
                  <Reveal>
                    <Link
                      to={`/tokenize/applications/${a.application_id}`}
                      className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40 hover:shadow-soft"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-ink">{a.name}</p>
                          <p className="truncate text-micro text-ink-subtle">
                            <span className="font-mono">{a.reference}</span> &middot; {a.product}
                          </p>
                        </div>
                        <StatusPill status={statusTone(a.status)} />
                      </div>

                      <p className="mt-2 text-micro text-ink-muted">{STATUS_SUMMARY[a.status]}</p>

                      {/* The tracker in miniature. Nine ticks, filled to where it has got to. */}
                      <div className="mt-3 flex items-center gap-1.5">
                        {a.stages.map((s) => (
                          <span
                            key={s.label}
                            title={s.label}
                            className={`h-1 flex-1 rounded-full ${
                              s.state === 'done'
                                ? 'bg-accent'
                                : s.state === 'current'
                                  ? 'bg-primary'
                                  : 'bg-surface-sunken'
                            }`}
                          />
                        ))}
                        <span className="tabular ml-1 shrink-0 text-nano text-ink-subtle">
                          {done}/{a.stages.length}
                        </span>
                      </div>
                    </Link>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {/* TOKENS ISSUED THROUGH THE LIFECYCLE, each with the asset behind it. */}
      {tokens.length > 0 ? (
        <section>
          <SectionTitle
            action={
              <Link
                to="/tokenize/portfolio"
                className="text-micro font-medium text-brand hover:underline"
              >
                All tokens
              </Link>
            }
          >
            Your tokenized assets
          </SectionTitle>
          <ul className="space-y-2.5">
            {tokens.map((t) => (
              <li key={t.token_id}>
                <Link
                  to={
                    t.underlying.application_id
                      ? `/tokenize/applications/${t.underlying.application_id}`
                      : '/tokenize/portfolio'
                  }
                  className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-ink">
                      {t.underlying.description ?? t.name}
                    </p>
                    <p className="truncate text-micro text-ink-subtle">
                      {t.symbol}
                      {t.underlying.held_by ? ` · held by ${t.underlying.held_by}` : ''}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="tabular text-sm font-medium text-ink">{formatUnits(t.units)}</p>
                    <p className="text-nano text-ink-subtle">
                      {formatUnits(t.units_available)} free
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* MY ASSETS. */}
      {assets.length > 0 ? (
        <section>
          <SectionTitle>My assets</SectionTitle>
          <ul className="space-y-3">
            {assets.map((a) => (
              <li key={a.id}>
                <Reveal>
                  <Link
                    to={`/tokenize/assets/${a.id}`}
                    className="block rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40 hover:shadow-soft"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-ink">{a.name}</p>
                        <p className="truncate text-micro text-ink-subtle">
                          {a.type} · {a.location}
                        </p>
                      </div>
                      <StatusPill
                        status={a.stage === 'offering' ? 'live' : a.stage}
                        label={STAGE_LABEL[a.stage]}
                      />
                    </div>
                    {a.capital ? (
                      <div className="mt-3 grid grid-cols-3 gap-3 border-t border-border pt-3">
                        <Mini label="Your allocation" value={num(a.capital.sponsorAllocation)} />
                        <Mini label="Raised" value={`KES ${a.capitalRaised}`} />
                        <Mini label="Approved value" value={`KES ${a.capital.approvedValue}`} />
                      </div>
                    ) : (
                      <p className="mt-2 text-micro text-ink-subtle">
                        With the trust for valuation and issuance.
                      </p>
                    )}
                  </Link>
                </Reveal>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* TOKENS HELD (investor side). A sponsor is also an investor in other people's assets. */}
      {holdings.length > 0 ? (
        <section>
          <SectionTitle
            action={
              <Link to="/portfolio" className="text-micro font-medium text-brand hover:underline">
                Portfolio
              </Link>
            }
          >
            Tokens you hold
          </SectionTitle>
          <ul className="space-y-2.5">
            {holdings.map((h) => (
              <li key={h.token_id}>
                <Link
                  to={`/tokenize/tokens/${h.token_id}`}
                  className="flex items-center justify-between gap-3 rounded-card border border-border bg-surface p-4 shadow-card transition hover:border-primary/40"
                >
                  <div className="min-w-0">
                    <p className="truncate font-mono text-xs text-ink-muted">
                      {h.ownership_reference}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="tabular text-sm font-medium text-ink">
                      {(h.fraction_bps / 100).toFixed(2)}%
                    </p>
                    <StatusPill
                      status={h.state}
                      label={
                        h.state === 'locked'
                          ? 'Pledged'
                          : h.state === 'issued'
                            ? 'Yours'
                            : undefined
                      }
                    />
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {empty ? (
        <EmptyState
          title="You have not tokenized anything yet"
          body="Bring property, land, a vehicle, or a receivable to the trust. It values the asset, issues the tokens, and allocates your share, without you selling the asset outright."
          action={{ label: 'Submit an asset', href: '/tokenize/new' }}
        />
      ) : null}
    </div>
  );
}

function Mini({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-nano text-ink-subtle">{label}</p>
      <p className="tabular truncate text-sm font-medium text-ink">{value}</p>
    </div>
  );
}

function Tile({ href, label, desc }: { href: string; label: string; desc: string }) {
  return (
    <Link
      to={href}
      className="rounded-card border border-border bg-surface p-3 shadow-card transition hover:border-primary/40 hover:shadow-soft"
    >
      <p className="text-sm font-medium text-ink">{label}</p>
      <p className="text-micro text-ink-subtle">{desc}</p>
    </Link>
  );
}
